import { connect } from "react-redux";
import LoadingBar from "react-top-loading-bar";
import React, { Fragment, useEffect, useRef } from "react";

import { handlePubSubKeys } from "../../../system";
import IdleUserModal from "../../../shared/hooks/IdleUserModal";
import {
  toastType,
  shopifyConstants,
  storeConstants,
  inventoryConstants,
} from "../../../constants";
import { useWebPubSub } from "../../../shared/hooks/useWebPubSub";
import {
  inventoryActions,
  shopifyActions,
  storeActions,
} from "../../../redux/actions";
import DraftConfirmationModal from "../../../views/SellAndTrade/Shared/DraftConfirmationModal";
import ToastPrintLabelModal from "../../../shared/components/ToastPrintLabel/ToastPrintLabelModal";
import ViewOrderNotification from "../../../shared/components/ToastPrintLabel/ViewOrderNotification";
import OrderDetailsModal from "../../../views/SellAndTrade/Orders/OrderDetailsModal/OrderDetailsModal";
import {
  customToast,
  handleMarkOrderAsOpen,
  handleMarkOrderAsReady,
  handleMarkOrderAsFulfilled,
  handleTransferToLocation,
} from "../../../shared/utility";
import { success } from "../../../redux/actions/actionsUtilities";

const MainWrapper = ({
  children,
  currentStore,
  isInventoryImporting,
  spinnerArray,
  auditCompleted,
  isAuthenticated,
  setCurrentStore,
  updateAuditData,
  isPrintLabelModal,
  getUserNotifications,
  isDraftConfirmationModal,
  updateShopifyOrderStatus,
  resetShopifyStateToEmpty,
  isShowShopifyOrderNotificationModal,
  shopifyOrderNotificationModalDetails,
  setIsShowShopifyOrderNotificationModal,
  activateImportInventorySpinner,
}) => {
  const loadingBarRef = useRef(null);
  const prevLengthRef = useRef(spinnerArray.length);
  // const { reset } = useIdleUserClose(10000);

  // Hook: Refresh when backend sends REFRESH_DATA
  useWebPubSub(currentStore, (keys) => {
    handlePubSubKeys(keys, {
      onNewShopifyOrder: (key) => {
        getUserNotifications(currentStore?.id);
        customToast(
          <span className="d-flex flex-column gap-2">
            {key.message.message}
            {<ViewOrderNotification orderId={key.message.orderId} />}
          </span>,
          toastType.INFO,
          7000
        );
      },
      onCustomerSyncWithShopifyUpdate: (key) => {
        setCurrentStore(key.message.body.data);
        customToast(key.message.body.message, toastType.INFO, 7000);
      },
      onShopifyIntegrationDisable: (key) => {
        if (currentStore?.id === key.message.body.data.id) {
          setCurrentStore(key.message.body.data);
          resetShopifyStateToEmpty();
        }
        customToast(key.message.body.message, toastType.INFO, 7000);
      },
      onAuditInitialized: (key) => {
        if (currentStore?.id === key.message.body.data.store.id) {
          updateAuditData(key.message.body.data);
        }
        customToast(key.message.body.message, toastType.INFO, 7000);
      },
      onAuditCompleted: (key) => {
        if (currentStore?.id === key.message.body.data.id) {
          auditCompleted(key.message.body.data.id);
        }
        customToast(key.message.body.message, toastType.INFO, 7000);
      },
      onInventoryAdded: (key) => {
        const payload = key?.message?.body?.data;

        const eventJobId = payload?.jobId
        const storeId = payload?.store?.id;

        if (
          !eventJobId ||
          !isInventoryImporting?.jobId ||
          String(eventJobId) !== String(isInventoryImporting.jobId)
        ) {
          return;
        }

        if (
          currentStore?.id &&
          storeId &&
          String(currentStore.id) !== String(storeId)
        ) {
          return;
        }

        activateImportInventorySpinner({
          isVisible: false,
          message: "",
          jobId: null,
        });
      },
    });
  });

  const toggleOrderDetailModal = () => {
    setIsShowShopifyOrderNotificationModal(
      !isShowShopifyOrderNotificationModal
    );
  };

  useEffect(() => {
    const prevLength = prevLengthRef.current;
    const currentLength = spinnerArray.length;

    if (prevLength === 0 && currentLength > 0) {
      loadingBarRef.current.continuousStart();
    } else if (currentLength === 0 && prevLength > 0) {
      loadingBarRef.current.complete();
    }

    // Update the previous length value
    prevLengthRef.current = currentLength;
  }, [spinnerArray]);

  return (
    <Fragment>
      <LoadingBar
        color="#6a18ed"
        ref={loadingBarRef}
        height={3}
        shadow={true}
      />
      {isDraftConfirmationModal ? <DraftConfirmationModal /> : ""}
      {isPrintLabelModal ? <ToastPrintLabelModal /> : ""}
      {isShowShopifyOrderNotificationModal ? (
        <OrderDetailsModal
          currentStore={currentStore}
          handleMarkOrderAsOpen={handleMarkOrderAsOpen}
          toggleOrderDetailModal={toggleOrderDetailModal}
          handleMarkOrderAsReady={handleMarkOrderAsReady}
          orderDetail={shopifyOrderNotificationModalDetails}
          updateShopifyOrderStatus={updateShopifyOrderStatus}
          defaultGridState={shopifyConstants.NOTIFICATION_MODAL}
          isShowOrderDetail={isShowShopifyOrderNotificationModal}
          handleMarkOrderAsFulfilled={handleMarkOrderAsFulfilled}
          handleTransferToLocation={handleTransferToLocation}
        />
      ) : (
        ""
      )}
      <div>
        <div>
          {isAuthenticated && <IdleUserModal />}
          {children}
        </div>
      </div>
    </Fragment>
  );
};

//-------Mapping the component's props to the reducer's state
const mapStateToProps = (state) => ({
  currentStore: state.store.currentStore,
  isInventoryImporting: state.inventory.isInventoryImporting,
  spinnerArray: state.system.spinnerArray,
  isPrintLabelModal: state.printer.isPrintLabelModal,
  isAuthenticated: state.authentication.isAuthenticated,
  isDraftConfirmationModal: state.transaction.isDraftConfirmationModal,
  isShowShopifyOrderNotificationModal:
    state.shopify.isShowShopifyOrderNotificationModal,
  shopifyOrderNotificationModalDetails:
    state.shopify.shopifyOrderNotificationModalDetails,
});

//-------Mapping the component's props to the related actions
const mapDispatchToProps = (dispatch) => ({
  getUserNotifications: (storeId) =>
    dispatch(storeActions.getUserNotifications(storeId)),
  setIsShowShopifyOrderNotificationModal: (value) =>
    dispatch(shopifyActions.setIsShowShopifyOrderNotificationModal(value)),
  setCurrentStore: (data) =>
    dispatch(success(data, storeConstants.SET_CURRENT_STORE)),
  updateAuditData: (data) =>
    dispatch(success(data, inventoryConstants.UPDATE_STOCK_AUDIT)),
  auditCompleted: (data) =>
    dispatch(success(data, storeConstants.UPDATE_AUDIT_SESSION_COMPLETE_FALSE)),
  updateShopifyOrderStatus: (
    storeId,
    data,
    gridState,
    toggleOrderDetailsModal
  ) =>
    dispatch(
      shopifyActions.updateShopifyOrderStatus(
        storeId,
        data,
        gridState,
        toggleOrderDetailsModal
      )
    ),
  resetShopifyStateToEmpty: () =>
    dispatch(shopifyActions.resetShopifyStateToEmpty()),
  activateImportInventorySpinner: (data) =>
    dispatch(inventoryActions.activateImportInventorySpinner(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(MainWrapper);
